import {
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import React from "react";

import { Person } from "../types/person";

interface Props {
  person: Person;
  isEdge: boolean;
  onClick: () => void;
  registerPosition?: (id: string, rect: DOMRect) => void;
  formatPersonSubtitle?: (person: Person) => string;
  personNodeWidth?: number;
}

export default function PersonCard({
  person,
  isEdge,
  onClick,
  registerPosition,
  formatPersonSubtitle,
  personNodeWidth = 160,
}: Props) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (ref.current && registerPosition) {
      registerPosition(person.id, ref.current.getBoundingClientRect());
    }
  });

  return (
    <Card variant="outlined" sx={{ my: 1, width: personNodeWidth }} ref={ref}>
      <CardActionArea onClick={onClick}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Avatar
            src={person.imageUrl}
            alt={person.name}
            sx={{ width: 56, height: 56, mb: 1 }}
          />
          <Typography
            variant="subtitle2"
            align="center"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
              px: 1, // Add horizontal padding to prevent text from touching edges
            }}
          >
            {person.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatPersonSubtitle
              ? formatPersonSubtitle(person)
              : person.birth || person.death
              ? `${person.birth ?? "–"} – ${person.death ?? ""}`
              : ""}
          </Typography>

          {isEdge && (
            <IconButton
              size="small"
              sx={{ position: "absolute", right: 4, top: 4 }}
              aria-label="expand ancestors"
            >
              <ArrowForwardIosIcon fontSize="inherit" />
            </IconButton>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
